import type { ReactNode, Ref } from 'react';

import { IconButton, ModalHeader, Search, VisuallyHidden } from '@coop/components';
import { XIcon } from '@coop/icons';
import classNames from 'classnames';
import { Suspense, useImperativeHandle, useRef } from 'react';

import { Modal } from '../../../common/components/atoms/Modal';
import { useSearchStateContext } from '../../../search/contexts/searchStateContext';
import styles from './SearchOverlay.module.scss';

interface SearchOverlayProps {
    onEnterPress: () => void;
    children: ReactNode;
    onOutsideClick: () => void;
    onClearClick: () => void;
    inputPlaceholder: string;
    announcerSlot?: React.ReactNode;
    inputAriaLabel?: React.ComponentPropsWithoutRef<typeof Search>['aria-label'];
    inputDescription?: string;
    submitLabel: string;
    dangerouslyDisableFocusBackToTrigger?: boolean;
    afterClose?: () => void;
    ref: Ref<HTMLInputElement>;
}

const SearchOverlay = (props: SearchOverlayProps) => {
    const { query, isOpen, clear, setQuery, setSearchType, show } = useSearchStateContext();

    const searchInputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(props.ref, () => searchInputRef.current as HTMLInputElement);

    return (
        <Modal
            className={styles.SearchOverlay}
            isOpen={isOpen}
            close={clear}
            afterClose={props.afterClose}
            initialFocusOnDialog={false}
            animation="fade"
            afterOutsideClick={props.onOutsideClick}
            dangerouslyDisableFocusBackToTrigger={props.dangerouslyDisableFocusBackToTrigger}
        >
            <VisuallyHidden>
                <ModalHeader autoFocus={false}>Sök</ModalHeader>
            </VisuallyHidden>
            <div
                className={classNames(
                    styles.SearchBarWrapper,
                    show && styles['SearchBarWrapper--hasResults'],
                )}
            >
                <Search
                    inputRef={searchInputRef}
                    submitButtonLabel={props.submitLabel}
                    value={query}
                    onChange={(text) => {
                        setSearchType('direct'); // always do direct searches from overlay when typing
                        setQuery(text);
                    }}
                    placeholder={props.inputPlaceholder}
                    description={props.inputDescription}
                    onEnterPress={() => {
                        props.onEnterPress();
                    }}
                    onEscapePress={() => {
                        clear();
                        searchInputRef.current?.blur();
                    }}
                    autoFocus
                    aria-label={props.inputAriaLabel}
                />
                <IconButton
                    theme="outlined"
                    icon={<XIcon />}
                    label="Stäng sökdialog"
                    size="default"
                    onClick={() => {
                        clear();
                        props.onClearClick();
                    }}
                />
            </div>
            {props.announcerSlot}
            <Suspense fallback={null}>
                {show && (
                    <div className={styles.SearchResults} data-testid="quick-search-results">
                        {props.children}
                    </div>
                )}
            </Suspense>
        </Modal>
    );
};

export default SearchOverlay;
